/**
 * Opportunity Filters Component
 * Provides filtering options for opportunities list
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Filter, X } from 'lucide-react';
import { useEffect, useState } from 'react';
export function OpportunityFilters({ filters, onFiltersChange, availableAgencies = [], availableSetAsides = [] }) {
    const [showFilters, setShowFilters] = useState(false);
    const [localFilters, setLocalFilters] = useState(filters);
    // Re-sync the dialog's working copy when the applied filters change from the
    // outside — e.g. the quick bar's Clear / "advanced filters" pill resets
    // `filters` to {}. Without this the dialog would keep showing (and silently
    // re-apply on Apply) filters that were already cleared elsewhere.
    useEffect(()=>{
        setLocalFilters(filters);
    }, [
        filters
    ]);
    // Count active filters
    const activeFilterCount = Object.values(filters).filter((v)=>v !== undefined && v !== '').length;
    const handleApply = ()=>{
        onFiltersChange(localFilters);
        setShowFilters(false);
    };
    const handleReset = ()=>{
        const emptyFilters = {};
        setLocalFilters(emptyFilters);
        onFiltersChange(emptyFilters);
        setShowFilters(false);
    };
    const handleCancel = ()=>{
        setLocalFilters(filters);
        setShowFilters(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative",
        children: [
            /*#__PURE__*/ _jsxs(Button, {
                variant: "outline",
                onClick: ()=>setShowFilters(!showFilters),
                className: "gap-2",
                "data-testid": "filter-button",
                children: [
                    /*#__PURE__*/ _jsx(Filter, {
                        className: "h-4 w-4"
                    }),
                    "Filters",
                    activeFilterCount > 0 && /*#__PURE__*/ _jsx("span", {
                        className: "ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none bg-primary text-primary-foreground rounded-full",
                        children: activeFilterCount
                    })
                ]
            }),
            showFilters && /*#__PURE__*/ _jsx("div", {
                className: "absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-popover text-popover-foreground rounded-lg shadow-lg border z-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "p-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-semibold",
                                    children: "Filter Opportunities"
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    onClick: handleCancel,
                                    className: "text-muted-foreground hover:text-foreground",
                                    "aria-label": "Close filters",
                                    children: /*#__PURE__*/ _jsx(X, {
                                        className: "h-5 w-5"
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium mb-1",
                                            children: "Agency"
                                        }),
                                        availableAgencies.length > 0 ? /*#__PURE__*/ _jsxs("select", {
                                            value: localFilters.agency || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    agency: e.target.value || undefined
                                                }),
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "agency-filter",
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "All Agencies"
                                                }),
                                                availableAgencies.map((agency)=>/*#__PURE__*/ _jsx("option", {
                                                        value: agency,
                                                        children: agency
                                                    }, agency))
                                            ]
                                        }) : /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: localFilters.agency || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    agency: e.target.value || undefined
                                                }),
                                            placeholder: "Enter agency name...",
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "agency-filter"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium mb-1",
                                            children: "Set-Aside Type"
                                        }),
                                        availableSetAsides.length > 0 ? /*#__PURE__*/ _jsxs("select", {
                                            value: localFilters.setAsideType || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    setAsideType: e.target.value || undefined
                                                }),
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "set-aside-filter",
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "All Set-Aside Types"
                                                }),
                                                availableSetAsides.map((setAside)=>/*#__PURE__*/ _jsx("option", {
                                                        value: setAside,
                                                        children: setAside
                                                    }, setAside))
                                            ]
                                        }) : /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: localFilters.setAsideType || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    setAsideType: e.target.value || undefined
                                                }),
                                            placeholder: "Enter set-aside type...",
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "set-aside-filter"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium mb-1",
                                            children: "NAICS Code"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: localFilters.naicsCode || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    naicsCode: e.target.value || undefined
                                                }),
                                            placeholder: "e.g., 541512",
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "naics-filter"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "6-digit industry code"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium mb-1",
                                            children: "Posted After"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "date",
                                            value: localFilters.postedAfter || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    postedAfter: e.target.value || undefined
                                                }),
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "posted-after-filter"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium mb-1",
                                            children: "Deadline Before"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "date",
                                            value: localFilters.deadlineBefore || '',
                                            onChange: (e)=>setLocalFilters({
                                                    ...localFilters,
                                                    deadlineBefore: e.target.value || undefined
                                                }),
                                            className: "w-full px-3 py-2 border rounded-md bg-background text-sm",
                                            "data-testid": "deadline-before-filter"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-6 flex gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: handleReset,
                                    className: "flex-1",
                                    size: "sm",
                                    children: "Reset"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: handleCancel,
                                    className: "flex-1",
                                    size: "sm",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    onClick: handleApply,
                                    className: "flex-1",
                                    size: "sm",
                                    children: "Apply"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
